import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Cloud } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { LogoIcon } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated, signIn } = useAuth();

  const navItems = [
    { label: "Product", href: "#product" },
    { label: "How it works", href: "#how-it-works" },
    { label: "Maker note", href: "#founder" },
    { label: "Join beta", href: "#early-access" },
  ];

  const scrollTo = (href) => {
    setIsOpen(false);
    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-sm border-b border-stone-100/50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo with wordmark */}
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <LogoIcon size={36} />
            <span className="font-semibold text-[#27272A]">Where was i</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollTo(item.href)}
                className="text-[#52525B] hover:text-[#F58227] text-sm font-medium transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* CTA */}
          <div className="flex items-center gap-2">
            {/* Open App button - first for mobile visibility */}
            <Link
              to="/app"
              className="inline-flex bg-[#F58227] text-white rounded-full px-3.5 py-1 text-xs font-semibold hover:bg-[#D96D19] transition-colors"
              data-testid="nav-start-app"
            >
              Open App
            </Link>

            {/* Sign in button */}
            {!isAuthenticated && (
              <button
                onClick={signIn}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-[#0CBCBF] bg-[#E0F7FA] hover:bg-[#B2EBF2] rounded-full transition-colors"
                data-testid="nav-sign-in"
              >
                <Cloud size={14} />
                <span>Sign in</span>
              </button>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 text-[#52525B] hover:text-[#F58227]"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-b border-stone-100 overflow-hidden"
          >
            <div className="px-4 py-4 space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => scrollTo(item.href)}
                  className="block w-full text-left px-4 py-3 text-[#52525B] hover:text-[#F58227] hover:bg-stone-50 rounded-xl text-sm font-medium transition-colors"
                >
                  {item.label}
                </button>
              ))}
              {!isAuthenticated && (
                <button
                  onClick={() => { setIsOpen(false); signIn(); }}
                  className="flex items-center justify-center gap-2 w-full bg-[#E0F7FA] text-[#0CBCBF] rounded-xl px-4 py-3 text-sm font-medium hover:bg-[#B2EBF2] transition-colors"
                >
                  <Cloud size={16} />
                  <span>Sign in</span>
                </button>
              )}
              <Link
                to="/app"
                onClick={() => {
                  setIsOpen(false);
                }}
                className="block w-full text-center bg-[#F58227] text-white rounded-xl px-4 py-3 text-sm font-semibold hover:bg-[#D96D19] transition-colors mt-4"
              >
                Open App
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
